import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Badge,
  List,
  Divider,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import dayjs from "dayjs";
import ConversationItem from "./components/ConversationItem";
import MessageBubble from "./components/MessageBubble";

import ChatHeader from "./components/ChatHeader";
import BookingContextBanner from "./components/BookingContextBanner";
import MessageInput from "./components/MessageInput";
import { Conversation, Message } from "shared/entities";
import ClientHeader from "../ClientHeader/ClientHeader";

const ClientMessages: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [searchQuery, setSearchQuery] = useState("");
  const [filterTab, setFilterTab] = useState(0);
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >("1");
  const [showChat, setShowChat] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Mock conversations data
  const [conversations] = useState<Conversation[]>([
    {
      id: "1",
      userId: "user1",
      userName: "Sarah M.",
      isOnline: true,
      lastMessage: "I've sent you a booking request. Can you accept it? 🔥",
      lastMessageTime: dayjs().subtract(2, "minute").toISOString(),
      unreadCount: 2,
      bookingId: "booking1",
    },
    {
      id: "2",
      userId: "user2",
      userName: "Michael B.",
      isOnline: false,
      lastMessage: "Thank you so much! Luna had a great time with you.",
      lastMessageTime: dayjs().subtract(1, "day").toISOString(),
      unreadCount: 0,
    },
    {
      id: "3",
      userId: "user3",
      userName: "John D.",
      isOnline: false,
      lastMessage: "Can you send me your availability for next month?",
      lastMessageTime: dayjs().subtract(2, "day").toISOString(),
      unreadCount: 1,
    },
    {
      id: "4",
      userId: "user4",
      userName: "Emma R.",
      isOnline: false,
      lastMessage: "Perfect! See you on July 1st 👍",
      lastMessageTime: dayjs().subtract(3, "day").toISOString(),
      unreadCount: 0,
    },
    {
      id: "5",
      userId: "user5",
      userName: "Jennifer L.",
      isOnline: true,
      lastMessage: "You: Thanks for the review! 🙏",
      lastMessageTime: dayjs().subtract(7, "day").toISOString(),
      unreadCount: 0,
    },
    {
      id: "6",
      userId: "user6",
      userName: "Amanda K.",
      isOnline: false,
      lastMessage: "Coco loves you! Can we book again? 🐱",
      lastMessageTime: dayjs().subtract(14, "day").toISOString(),
      unreadCount: 0,
    },
  ] as unknown as Conversation[]);

  // Mock messages data
  const [messagesData, setMessagesData] = useState<Record<string, Message[]>>({
    "1": [
      {
        id: "msg1",
        senderId: "user1",
        senderName: "Sarah M.",
        content:
          "Hi Jose! I found your profile and you look perfect for taking care of my two dogs while I'm away! 🐶",
        timestamp: dayjs().subtract(25, "minute").toISOString(),
        isOwn: false,
      },
      {
        id: "msg2",
        senderId: "user1",
        senderName: "Sarah M.",
        content:
          "Are you available June 15-17? It would be house visits, twice a day. They're very friendly!",
        timestamp: dayjs().subtract(24, "minute").toISOString(),
        isOwn: false,
      },
      {
        id: "msg3",
        senderId: "sitter",
        senderName: "Jose L.",
        content: "Hi Sarah! Thank you for reaching out! 😊",
        timestamp: dayjs().subtract(20, "minute").toISOString(),
        isOwn: true,
      },
      {
        id: "msg4",
        senderId: "sitter",
        senderName: "Jose L.",
        content:
          "Yes, I'm available for those dates! I'd love to take care of your dogs. Can you tell me more about them? Breed, age, any special needs?",
        timestamp: dayjs().subtract(19, "minute").toISOString(),
        isOwn: true,
      },
      {
        id: "msg5",
        senderId: "user1",
        senderName: "Sarah M.",
        content:
          "Great! Max is a 5-year-old Golden Retriever and Bella is a 3-year-old Labrador. Both are very friendly and well-trained. They need to be fed twice a day and love their walks!",
        timestamp: dayjs().subtract(15, "minute").toISOString(),
        isOwn: false,
      },
      {
        id: "msg6",
        senderId: "sitter",
        senderName: "Jose L.",
        content:
          "Perfect! They sound wonderful. I have plenty of experience with both breeds. Looking forward to meeting them! 🐕",
        timestamp: dayjs().subtract(10, "minute").toISOString(),
        isOwn: true,
      },
      {
        id: "msg7",
        senderId: "user1",
        senderName: "Sarah M.",
        content: "I've sent you a booking request. Can you accept it? 🔥",
        timestamp: dayjs().subtract(2, "minute").toISOString(),
        isOwn: false,
      },
    ],
    "2": [
      {
        id: "msg1",
        senderId: "user2",
        senderName: "Michael B.",
        content:
          "Hey! Just wanted to say thank you for taking such great care of Luna last week.",
        timestamp: dayjs().subtract(1, "day").toISOString(),
        isOwn: false,
      },
      {
        id: "msg2",
        senderId: "user2",
        senderName: "Michael B.",
        content: "Thank you so much! Luna had a great time with you.",
        timestamp: dayjs().subtract(1, "day").toISOString(),
        isOwn: false,
      },
    ],
    "3": [
      {
        id: "msg1",
        senderId: "user3",
        senderName: "John D.",
        content: "Hi there! Hope you're doing well.",
        timestamp: dayjs().subtract(2, "day").toISOString(),
        isOwn: false,
      },
      {
        id: "msg2",
        senderId: "user3",
        senderName: "John D.",
        content: "Can you send me your availability for next month?",
        timestamp: dayjs().subtract(2, "day").toISOString(),
        isOwn: false,
      },
    ],
  } as unknown as Record<string, Message[]>);

  const selectedConversation = conversations.find(
    (c) => c.id === selectedConversationId,
  );

  const currentMessages = selectedConversationId
    ? messagesData[selectedConversationId] || []
    : [];

  // Filter conversations based on search and tab
  const filteredConversations = conversations.filter((conv) => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (
        !conv.userName.toLowerCase().includes(query) &&
        !conv.lastMessage.toLowerCase().includes(query)
      ) {
        return false;
      }
    }

    // Tab filter
    if (filterTab === 1 && conv.unreadCount === 0) {
      return false; // Unread only
    }
    if (filterTab === 2 && !conv.bookingId) {
      return false; // Bookings only
    }

    return true;
  });

  const unreadCount = conversations.filter((c) => c.unreadCount > 0).length;

  const handleConversationClick = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    if (isMobile) {
      setShowChat(true);
    }
  };

  const handleBackToList = () => {
    setShowChat(false);
  };

  const handleSendMessage = (messageContent: string) => {
    if (!selectedConversationId) return;

    const newMessage: Message = {
      id: `msg${Date.now()}`,
      conversationId: selectedConversationId,
      senderId: "sitter",
      senderName: "Jose L.",
      content: messageContent,
      timestamp: dayjs().toISOString(),
      isOwn: true,
      createdAt: new Date(),
    };

    setMessagesData((prev) => ({
      ...prev,
      [selectedConversationId]: [
        ...(prev[selectedConversationId] || []),
        newMessage,
      ],
    }));
  };

  const handleViewBookingDetails = (bookingId: string) => {
    console.log("View booking details:", bookingId);
    // Future: Navigate to booking details page
  };

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [currentMessages]);

  // Show conversations sidebar
  const renderConversationsSidebar = () => (
    <Box
      sx={{
        width: isMobile ? "100%" : 380,
        height: "100%",
        borderRight: isMobile ? "none" : "1px solid #E0E0E0",
        display: isMobile && showChat ? "none" : "flex",
        flexDirection: "column",
        backgroundColor: "#FFFFFF",
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, pb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          Messages
        </Typography>

        {/* Search bar */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search conversations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              backgroundColor: "#F5F5F5",
              "&:hover": {
                backgroundColor: "#EEEEEE",
              },
              "&.Mui-focused": {
                backgroundColor: "#FFFFFF",
              },
            },
          }}
        />
      </Box>

      {/* Filter tabs */}
      <Box sx={{ px: 2, borderBottom: "1px solid #E0E0E0" }}>
        <Tabs
          value={filterTab}
          onChange={(_, newValue) => setFilterTab(newValue)}
          sx={{
            minHeight: 42,
            "& .MuiTab-root": {
              minHeight: 42,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
            },
          }}
        >
          <Tab label="All" />
          <Tab
            label={
              <Badge badgeContent={unreadCount} color="primary">
                <span>Unread</span>
              </Badge>
            }
          />
          <Tab label="Bookings" />
        </Tabs>
      </Box>

      {/* Conversations list */}
      <List
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 0,
          "& .MuiListItemButton-root": {
            px: 0,
          },
        }}
      >
        {filteredConversations.map((conversation, index) => (
          <React.Fragment key={conversation.id}>
            <ConversationItem
              conversation={conversation}
              isActive={conversation.id === selectedConversationId}
              onClick={handleConversationClick}
            />
            {index < filteredConversations.length - 1 && (
              <Divider sx={{ my: 0 }} />
            )}
          </React.Fragment>
        ))}

        {filteredConversations.length === 0 && (
          <Box
            sx={{
              p: 4,
              textAlign: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No conversations found
            </Typography>
          </Box>
        )}
      </List>
    </Box>
  );

  // Show chat area
  const renderChatArea = () => {
    if (!selectedConversation) {
      return (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#FAF8F5",
          }}
        >
          <Typography variant="h6" color="text.secondary">
            Select a conversation to start messaging
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          flex: 1,
          display: isMobile && !showChat ? "none" : "flex",
          flexDirection: "column",
          height: "100%",
          backgroundColor: "#FAF8F5",
        }}
      >
        {/* Back button for mobile */}
        {isMobile && (
          <Box
            sx={{
              p: 1,
              backgroundColor: "#FFFFFF",
              borderBottom: "1px solid #E0E0E0",
            }}
          >
            <IconButton onClick={handleBackToList}>
              <ArrowBackIcon />
            </IconButton>
          </Box>
        )}

        {/* Chat header */}
        <ChatHeader
          userName={selectedConversation.userName}
          userAvatar={selectedConversation.userAvatar}
          isOnline={selectedConversation.isOnline}
        />

        {/* Booking context banner */}
        {selectedConversation.bookingId && (
          <BookingContextBanner
            bookingId={selectedConversation.bookingId}
            dates="Jun 15-17, 2025"
            petInfo="2 Dogs"
            price={180}
            status="pending"
            onViewDetails={handleViewBookingDetails}
          />
        )}

        {/* Messages area */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 3,
            py: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Date divider */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              my: 2,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                px: 2,
                py: 0.5,
                backgroundColor: "rgba(0, 0, 0, 0.08)",
                borderRadius: 2,
                color: "text.secondary",
                fontWeight: 600,
              }}
            >
              Today
            </Typography>
          </Box>

          {/* Messages */}
          {currentMessages.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))}

          <div ref={messagesEndRef} />
        </Box>

        {/* Message input */}
        <MessageInput onSendMessage={handleSendMessage} />
      </Box>
    );
  };

  return (
    <>
      <ClientHeader />
      <Container
        maxWidth="xl"
        sx={{
          py: 4,
          height: isMobile ? "calc(100vh - 32px)" : "calc(100vh - 100px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Paper
          elevation={2}
          sx={{
            flex: 1,
            display: "flex",
            overflow: "hidden",
            borderRadius: 2,
          }}
        >
          {renderConversationsSidebar()}
          {renderChatArea()}
        </Paper>
      </Container>
    </>
  );
};

export default ClientMessages;
